import { useEffect, useRef, useState } from "react";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, type UIMessage } from "ai";
import { Link } from "react-router-dom";
import { Trash2, Plus, RotateCcw, ArrowLeft } from "lucide-react";
import { EditorialMarkdown } from "@/components/buddy/EditorialMarkdown";

import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
  ConversationEmptyState,
} from "@/components/ai-elements/conversation";
import { Message, MessageContent } from "@/components/ai-elements/message";
import {
  PromptInput,
  PromptInputTextarea,
  PromptInputFooter,
  PromptInputSubmit,
} from "@/components/ai-elements/prompt-input";
import { Shimmer } from "@/components/ai-elements/shimmer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { AiGeneratedBadge } from "@/components/AiGeneratedBadge";

import logo from "@/assets/gotobuddy-logo.png";
import PageMeta from "@/components/PageMeta";
import { supabase } from "@/integrations/supabase/client";

const DEFAULT_WATCHLIST = [
  "RENDER", "TAO", "ONDO", "ICP", "NEAR", "LINK", "IO", "ATH", "FIL", "GRASS",
];

const STORAGE_KEY_MESSAGES = "gotobuddy:messages:v1";
const STORAGE_KEY_WATCHLIST = "gotobuddy:watchlist:v1";
const STORAGE_KEY_SESSION = "gotobuddy:session_id";

function getSessionId(): string {
  try {
    let id = localStorage.getItem(STORAGE_KEY_SESSION);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(STORAGE_KEY_SESSION, id);
    }
    return id;
  } catch {
    return crypto.randomUUID();
  }
}

function loadMessages(): UIMessage[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_MESSAGES);
    if (!raw) return [];
    return JSON.parse(raw) as UIMessage[];
  } catch {
    return [];
  }
}

function loadWatchlist(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_WATCHLIST);
    if (!raw) return DEFAULT_WATCHLIST;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length ? parsed : DEFAULT_WATCHLIST;
  } catch {
    return DEFAULT_WATCHLIST;
  }
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_KEY = (import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ||
  import.meta.env.VITE_SUPABASE_ANON_KEY) as string;

export default function Buddy() {
  const [watchlist, setWatchlist] = useState<string[]>(() => loadWatchlist());
  const [newToken, setNewToken] = useState("");
  const [input, setInput] = useState("");
  const initialMessages = useRef<UIMessage[]>(loadMessages());
  const watchlistRef = useRef(watchlist);
  watchlistRef.current = watchlist;
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const transport = useRef(
    new DefaultChatTransport({
      api: `${SUPABASE_URL}/functions/v1/gotobuddy-chat`,
      headers: { Authorization: `Bearer ${SUPABASE_KEY}` },
      body: () => ({ watchlist: watchlistRef.current }),
    }),
  ).current;

  const messagesRef = useRef<UIMessage[]>(initialMessages.current);
  const sessionIdRef = useRef<string>("");

  const syncToCommandCentre = () => {
    try {
      if (!sessionIdRef.current) sessionIdRef.current = getSessionId();
      const payloadMessages = messagesRef.current.map((m) => ({
        role: m.role,
        content: m.parts
          .map((p) => (p.type === "text" ? p.text : ""))
          .join(""),
      }));
      if (!payloadMessages.length) return;
      void supabase.functions
        .invoke("sync-command-centre", {
          body: {
            conversationId: sessionIdRef.current,
            sessionId: sessionIdRef.current,
            messages: payloadMessages,
            updatedAt: new Date().toISOString(),
          },
        })
        .catch((err) => console.error("Command Centre sync failed:", err));
    } catch (err) {
      console.error("Command Centre sync failed:", err);
    }
  };

  const { messages, sendMessage, status, setMessages, stop } = useChat({
    id: "gotobuddy-main",
    messages: initialMessages.current,
    transport,
    onError: (err) => toast.error(err.message || "Buddy hit a snag"),
    onFinish: () => {
      // Fire-and-forget; runs after the assistant turn completes
      setTimeout(syncToCommandCentre, 0);
    },
  });

  // Persist messages
  useEffect(() => {
    messagesRef.current = messages;
    try {
      localStorage.setItem(STORAGE_KEY_MESSAGES, JSON.stringify(messages));
    } catch { /* ignore quota */ }
  }, [messages]);

  // Persist watchlist
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_WATCHLIST, JSON.stringify(watchlist));
    } catch { /* ignore */ }
  }, [watchlist]);

  // Focus textarea on mount, after sends, after stream completes
  useEffect(() => {
    if (status === "ready" || status === "error") textareaRef.current?.focus();
  }, [status]);

  const isLoading = status === "submitted" || status === "streaming";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || isLoading) return;
    setInput("");
    await sendMessage({ text });
  };

  const addToken = () => {
    const t = newToken.trim().toUpperCase();
    if (!t) return;
    if (watchlist.includes(t)) {
      toast.info(`${t} is already on your list`);
      return;
    }
    if (watchlist.length >= 15) {
      toast.error("Max 15 tokens — remove one first");
      return;
    }
    setWatchlist((w) => [...w, t]);
    setNewToken("");
  };

  const removeToken = (t: string) => setWatchlist((w) => w.filter((x) => x !== t));
  const resetWatchlist = () => setWatchlist(DEFAULT_WATCHLIST);

  const clearChat = () => {
    setMessages([]);
    localStorage.removeItem(STORAGE_KEY_MESSAGES);
  };

  const quickPrompts = [
    "Daily Signals",
    "Rebalance my Top 10",
    `Deep dive on ${watchlist[0] ?? "RENDER"}`,
    "What should I buy on Swyftx today?",
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageMeta
        title="GoToBuddy — AI Crypto Assistant for Aussie Investors"
        description="Chat with GoToBuddy, an AI research assistant tuned for the Australian crypto market: daily signals, rebalance ideas and token deep-dives."
        path="/buddy"
      />
      <header className="border-b border-border/60 bg-card/40 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-4 h-4" /> GoToCoins
          </Link>
          <div className="ml-auto flex items-center gap-3">
            <img src={logo} alt="GoToBuddy" width={36} height={36} className="rounded-lg" />
            <div className="leading-tight">
              <div className="font-semibold">GoToBuddy</div>
              <div className="text-xs text-muted-foreground">AI Crypto Research</div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        {/* Chat */}
        <section className="flex flex-col h-[calc(100vh-180px)] min-h-[520px]">
          <AiGeneratedBadge className="mb-3" />
          <Conversation className="flex-1 rounded-xl border border-border/60 bg-card/30">
            <ConversationContent>
              {messages.length === 0 ? (
                <ConversationEmptyState
                  icon={<img src={logo} alt="" width={72} height={72} />}
                  title="GoToBuddy"
                  description="An editorial AI briefing on your personal Top 10. Ask for daily signals, a deep dive, or a rebalance."
                />
              ) : (
                messages.map((m) => {
                  const text = m.parts
                    .map((p) => (p.type === "text" ? p.text : ""))
                    .join("");
                  return (
                    <Message key={m.id} from={m.role === "user" ? "user" : "assistant"}>
                      <MessageContent>
                        {m.role === "user" ? (
                          <p className="whitespace-pre-wrap">{text}</p>
                        ) : (
                          <EditorialMarkdown content={text} />
                        )}
                      </MessageContent>
                    </Message>
                  );
                })
              )}
              {status === "submitted" && (
                <Message from="assistant">
                  <MessageContent>
                    <Shimmer>Composing briefing…</Shimmer>
                  </MessageContent>
                </Message>
              )}
            </ConversationContent>
            <ConversationScrollButton />
          </Conversation>

          {messages.length === 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {quickPrompts.map((p) => (
                <Button
                  key={p}
                  variant="outline"
                  size="sm"
                  onClick={() => sendMessage({ text: p })}
                  disabled={isLoading}
                >
                  {p}
                </Button>
              ))}
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-3">
            <PromptInput onSubmit={() => { /* handled via form */ }}>
              <PromptInputTextarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask GoToBuddy anything… (e.g. 'Add SOL', 'Daily signals', 'Deep dive on TAO')"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e as unknown as React.FormEvent);
                  }
                }}
              />
              <PromptInputFooter className="justify-between">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={clearChat}
                  disabled={isLoading || messages.length === 0}
                >
                  Clear chat
                </Button>
                <PromptInputSubmit
                  status={status}
                  disabled={!input.trim() && !isLoading}
                  onClick={(e) => {
                    if (isLoading) {
                      e.preventDefault();
                      stop();
                    }
                  }}
                />
              </PromptInputFooter>
            </PromptInput>
          </form>

          <p className="mt-3 text-[11px] text-muted-foreground text-center">
            Educational only. Not financial advice. Crypto is volatile — DYOR.
          </p>
        </section>

        {/* Watchlist sidebar */}
        <aside className="space-y-4">
          <Card className="p-4 bg-card/40">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="font-semibold">My Top {watchlist.length}</h2>
                <p className="text-xs text-muted-foreground">Saved in this browser</p>
              </div>
              <Button variant="ghost" size="icon-sm" onClick={resetWatchlist} title="Reset to default" aria-label="Reset watchlist">
                <RotateCcw className="w-4 h-4" />
              </Button>
            </div>

            <div className="flex flex-wrap gap-2 mb-3">
              {watchlist.map((t, i) => (
                <Badge
                  key={t}
                  variant="secondary"
                  className="flex items-center gap-1 pl-2 pr-1 py-1 text-xs font-mono"
                >
                  <span className="opacity-60 mr-1">{i + 1}.</span>
                  {t}
                  <button
                    type="button"
                    onClick={() => removeToken(t)}
                    className="ml-1 rounded hover:bg-destructive/20 p-0.5"
                    aria-label={`Remove ${t}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>

            <div className="flex gap-2">
              <Input
                value={newToken}
                onChange={(e) => setNewToken(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addToken())}
                placeholder="Add ticker (e.g. SOL)"
                className="h-9"
                maxLength={10}
              />
              <Button onClick={addToken} size="sm" disabled={watchlist.length >= 15}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            <p className="text-[11px] text-muted-foreground mt-3">
              Buddy uses this list to personalise daily signals, deep dives & rebalances.
            </p>
          </Card>

          <Card className="p-4 bg-card/40 text-xs text-muted-foreground space-y-2">
            <div className="font-semibold text-foreground">Try saying:</div>
            <div>• "Daily Signals"</div>
            <div>• "Deep dive on RENDER"</div>
            <div>• "Rebalance for AI/DePIN exposure"</div>
            <div>• "Add SOL" / "Remove FIL"</div>
            <div>• "What's the best AUSTRAC exchange right now?"</div>
          </Card>
        </aside>
      </main>
    </div>
  );
}
